import { useMemo } from 'react';
import { useLocaleContext } from '../../context/localeContext';
import InfoSmall from '../../icons/Info/InfoSmall.svg';
import { TypographyStyles } from '../../stylings';
import { Locale } from '../../utils/internationalization/localeHelper';
import { useTooltip } from './Tooltip';
import {
  PriceTooltipArrow,
  PriceTooltipBodyText,
  PriceTooltipBubble,
  PriceTooltipContainer,
  PriceTooltipHeaderText,
  PriceTooltipIcon,
} from './Tooltip.styles';

export type LastOfferedTooltipProps = {
  fromDate?: string | null;
  toDate?: string | null;
  wasPrice?: string | null;
  lastOfferedAtDate?: string | null;
};

export function LastOfferedTooltip({ fromDate, toDate, wasPrice, lastOfferedAtDate }: LastOfferedTooltipProps) {
  const { locale } = useLocaleContext();
  const { isOpen, handleClick, buttonRef, containerRef, setArrowRef, styles, attributes } = useTooltip();

  const convertESTToClientTimezone = (dateString: string, clientTimezone: string) => {
    const [month, day] = dateString.split('/');
    const fullYear = new Date(dateString).getFullYear();

    const dateInEST = new Date(`${fullYear}-${month}-${day}T00:00:00-05:00`);
    return new Date(dateInEST.toLocaleString('en-US', { timeZone: clientTimezone }));
  };

  function setEndOfDay(date: Date): Date {
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 0);
    return endOfDay;
  }

  const isTooltipVisible = useMemo(() => {
    if (!fromDate || !lastOfferedAtDate) return false;

    const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fromDateInClientTimezone = convertESTToClientTimezone(fromDate, clientTimezone);
    const currentDateInClientTimezone = new Date(new Date().toLocaleString('en-US', { timeZone: clientTimezone }));

    let toDateInClientTimezone: Date | null = null;
    if (toDate) {
      toDateInClientTimezone = convertESTToClientTimezone(toDate, clientTimezone);
    }

    const endOfDay = toDateInClientTimezone
      ? setEndOfDay(toDateInClientTimezone)
      : setEndOfDay(new Date(fromDateInClientTimezone));

    return (
      currentDateInClientTimezone >= fromDateInClientTimezone && (currentDateInClientTimezone <= endOfDay || !toDate)
    );
  }, [fromDate, toDate, lastOfferedAtDate]);

  if (![Locale.EN_US].includes(locale as string)) {
    return null;
  }

  return (
    <PriceTooltipContainer role="tooltip">
      {isTooltipVisible && (
        <PriceTooltipIcon
          onClick={handleClick}
          ref={buttonRef}
          aria-label="Last Offered tooltip"
          type="button"
          data-testid="lastOfferedTooltipButton"
        >
          <InfoSmall />
        </PriceTooltipIcon>
      )}

      {isOpen && isTooltipVisible && (
        <PriceTooltipBubble
          ref={containerRef}
          style={styles.popper}
          {...attributes.popper}
          data-testid="lastOfferedTooltipModal"
        >
          <PriceTooltipArrow ref={setArrowRef} style={styles.arrow} className="arrow" />
          <PriceTooltipHeaderText>Last Offered</PriceTooltipHeaderText>
          <PriceTooltipBodyText
            text={`Last offered online at ${wasPrice} on ${lastOfferedAtDate}`}
            fontSize={TypographyStyles.Body.Tiny.Regular}
          />
        </PriceTooltipBubble>
      )}
    </PriceTooltipContainer>
  );
}

export default LastOfferedTooltip;
